import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Dropdown } from '../../../../components/shareable/dropdownMenu';
import { useDashboard } from '../../../../context/dashboardContext';
import { useAuth } from '../../../../context/authContext';
import { MoreHorizontal, Save, X, Trash2, ChevronDown, ChevronRight, GripVertical, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import PDFModal from './PDFModal';
import ConfirmModal from '../../../../components/shareable/confirmModal';
import CustomModal from './customModal';
import { LoadingModal } from '../../../../components/shareable/loadingModal';
import { useGlobalIndices, useSectionItemsWithIndices } from '../../../../utils/helper';
import { generatePdfTemplate } from './pdfTemplate';
import { parseTaxPercent } from '../../../../utils/quoteTotals';
import {
  QUOTE_DETAIL_FIELDS,
  EMPTY_DROPDOWN_OPTIONS,
  INSTALLATION_COST_FIELDS,
  buildDropdownOptions,
  computeLaborTotal,
  computeLodgingTotal,
  computeInstallationTotal,
  getTechOptions,
  getDayOptions,
  sanitizeMoneyInput,
} from './quoteDetailFields';

// Helper function to add commas to numbers
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const TableComponent = forwardRef(({ data = [], onSave, onClear, onExit, isAddingNew, onCancelAddNew, customerData, quoteNumber, discount, sawDetails, workBedHeight, infeedRef, outfeedRef, leftBoxItems, rightBoxItems, checkedItems, leftBoxConveyorStyle, rightBoxConveyorStyle, generateConveyorFilename, motorSelections, motorVariantByBox, isGeneratingPdf, setIsGeneratingPdf, sawRef, arrowRef, quoteDetails, setQuoteDetails, quoteBrochure, selectedAdditionalEmails, selectedContact }, ref) => {
  const [editingId, setEditingId] = useState(null);
  const { generateQuotePdf, getLayoutImages, getGlobalOptions } = useDashboard();
  // Stamped onto the PDF's company box as the contact name/email — always the user
  // generating the PDF, on create and update alike.
  const { user } = useAuth();
  const [editedData, setEditedData] = useState({});
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  // Frozen quote_number captured at PDF-generation time. The parent's
  // quoteNumber state can change while the modal is open (resetCreateForm
  // pre-allocates the next number), so emailing must use this snapshot to
  // match the S3 key we just wrote.
  const [pdfQuoteNumber, setPdfQuoteNumber] = useState('');
  const [grandTotal, setGrandTotal] = useState(0);
  
  // State for confirm modals
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // State for section visibility - default to open if they have items
  const [openSections, setOpenSections] = useState({ infeed: false, outfeed: false, new: false, additional: false });

  // --- Inline editing of the Additional Quote Details panel -----------------------
  // The EDIT button in that panel's header turns every row into the right kind of input
  // (dropdown / text / dollars) so the terms can be corrected in place without hunting
  // for the Additional Quote Details popup. Edits write straight into quoteDetails, which
  // is what Create Quote and Update Quote submit — so those buttons save the details too,
  // whether or not SAVE was pressed first.
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  // quoteDetails as it was when EDIT was pressed, so Cancel is a real undo.
  const [detailsSnapshot, setDetailsSnapshot] = useState(null);
  const [detailOptions, setDetailOptions] = useState(EMPTY_DROPDOWN_OPTIONS);
  const [detailInstallationData, setDetailInstallationData] = useState({});
  const [detailOptionsLoaded, setDetailOptionsLoaded] = useState(false);
  // { customType, path } while the "Enter Custom ..." prompt is open.
  const [detailCustomPrompt, setDetailCustomPrompt] = useState(null);
  // Rows as they stood when EDIT was pressed. A row only exists while its value is
  // non-empty, so without this, backspacing a field to blank would make its row vanish
  // out from under the cursor with no way to type the value back in.
  const [detailRowsAtEditStart, setDetailRowsAtEditStart] = useState([]);

  // State for drag and drop
  const [draggedItem, setDraggedItem] = useState(null);

  // Holds a follow-up callback to fire after a successful PDF generation. The
  // Update Quote button uses this to navigate away once the PDF is ready.
  const pendingSaveRef = useRef(null);
  // Optional override for the quote number used during PDF generation. After
  // an update the server bumps the version, so the caller passes the new
  // quote number here to keep the PDF aligned with the freshly-saved record.
  const quoteNumberOverrideRef = useRef(null);
  useImperativeHandle(ref, () => ({
    triggerGenerateAndSave: (saveFn, overrideQuoteNumber) => {
      pendingSaveRef.current = typeof saveFn === 'function' ? saveFn : null;
      quoteNumberOverrideRef.current = overrideQuoteNumber || null;
      handleGeneratePdf();
    }
  }));

  const headers = [ '#ID', 'Type', 'Part Number', 'Description', 'Qty', 'List Price', 'Discount', 'Actual Price', 'Ext Price', 'Actions' ];

  // Column widths for consistent layout
  const columnWidths = [ 'w-12', 'w-24', 'w-40', 'w-64', 'w-20', 'w-28', 'w-24', 'w-28', 'w-28', 'w-16' ];
  const optionSpecialLabels = ['MT', 'TA', 'STK', 'PW', 'VR', 'CP'];

  // Drop-zone identifiers — each maps to a section/sub-table in the table.
  const VALID_DROP_SECTIONS = ['infeed-primary', 'infeed-options', 'outfeed-primary', 'outfeed-options', 'new'];

  // User-added items (added via the "+ New Item" button) can be dragged
  // between sections. Conveyor-derived items are tied to the diagram and
  // must not be reordered through DnD.
  const isUserMovable = (item) => Boolean(item?.isUserAdded);

  // Resolve the section a row currently lives in. `userSection` (set when the
  // user drags an item into a section) takes precedence over the conveyor-
  // derived defaults (boxName + specialLabel).
  const getSectionFor = (item) => {
    if (!item || item.isDeleted) return null;
    if (item.userSection && VALID_DROP_SECTIONS.includes(item.userSection)) return item.userSection;
    if (item.boxName === 'left') {
      return optionSpecialLabels.includes(item.specialLabel) ? 'infeed-options' : 'infeed-primary';
    }
    if (item.boxName === 'right') {
      return optionSpecialLabels.includes(item.specialLabel) ? 'outfeed-options' : 'outfeed-primary';
    }
    return 'new';
  };

  // Pretty label for toast messages.
  const sectionLabel = (key) => ({
    'infeed-primary': 'Primary Quote InFeed',
    'infeed-options': 'Primary Quote InFeed Options',
    'outfeed-primary': 'Primary Quote OutFeed',
    'outfeed-options': 'Primary Quote OutFeed Options',
    'new': 'New Items',
  })[key] || key;

  // Sort a section's items by sortOrder (asc), falling back to natural index
  // for rows without an explicit sortOrder.
  const applySortOrder = useCallback((items) => {
    return items
      .map((item, idx) => ({ item, idx, eff: item.sortOrder != null ? Number(item.sortOrder) : idx }))
      .sort((a, b) => (a.eff - b.eff) || (a.idx - b.idx))
      .map(x => x.item);
  }, []);

  // Function to combine STK/TA option rows so quantity reflects per-portion selections
  // Also groups label-option items (PW, VR, CP) by part name
  const combineStockArmItems = useCallback((items) => {
    const combinedSpecialMap = new Map();
    const combinedLabelOptionMap = new Map();
    const nonSpecialItems = [];
    
    items.forEach(item => {
      const description = item.description || '';
      const label = item.specialLabel || '';
      const partName = item.partName || item.partNumber || '';
      
      const isStockArm = description.toUpperCase().includes('STOCK ARM') || label === 'STK';
      const isTiltArm = description.toUpperCase().includes('TILT ARM') || label === 'TA';
      const isLabelOption = ['PW', 'VR', 'CP'].includes(label) && item.labelOptionKey;

      const shouldCombine = isStockArm || isTiltArm;
      
      if (shouldCombine) {
        // Include partName so different STK/TA variants (which share a description
        // but have distinct part numbers) stay as separate rows.
        const key = `${label || 'UNKNOWN'}::${partName}::${description}`;
        if (combinedSpecialMap.has(key)) {
          const existing = combinedSpecialMap.get(key);
          existing.combinedIds = existing.combinedIds || [existing.id];
          existing.combinedIds.push(item.id);
          // STK manual override: any item in the group carrying manualQty wins.
          if (label === 'STK' && item.manualQty != null) {
            existing.qty = parseFloat(item.manualQty) || 0;
            existing.manualQty = item.manualQty;
          } else if (label !== 'STK' || existing.manualQty == null) {
            existing.qty = (parseFloat(existing.qty) || 0) + (parseFloat(item.qty) || 0);
          }
        } else {
          const fresh = { ...item };
          if (label === 'STK' && item.manualQty != null) {
            fresh.qty = parseFloat(item.manualQty) || 0;
          }
          combinedSpecialMap.set(key, fresh);
        }
      } else if (isLabelOption) {
        let key;

        if (label === 'VR') {
          // For VR, group only by label
          key = 'VR';
        } else if (label === 'PW' || label === 'CP') {
          // PW and CP: group by part name
          if (!partName) { 
            nonSpecialItems.push(item); 
            return; 
          }
          key = `${label}::${partName}`;
        } else {
          nonSpecialItems.push(item);
          return;
        }
        
        if (combinedLabelOptionMap.has(key)) {
          const existing = combinedLabelOptionMap.get(key);
          existing.qty = (parseFloat(existing.qty) || 0) + (parseFloat(item.qty) || 0);

          // Sum up the ext prices for VR items
          if (label === 'VR') { existing.extPrice = (parseFloat(existing.extPrice) || 0) + (parseFloat(item.extPrice) || (parseFloat(item.qty) || 0) * (parseFloat(item.actualPrice) || 0));
          } else { existing.extPrice = existing.qty * (existing.actualPrice || 0); }
          // Merge labelOptionKeys and portionIndices, removing duplicates
          const existingKeysArray = existing.labelOptionKeys || (existing.labelOptionKey ? [existing.labelOptionKey] : []);
          const existingKeys = new Set(existingKeysArray);
          if (item.labelOptionKeys) {
            item.labelOptionKeys.forEach(k => existingKeys.add(k));
          } else if (item.labelOptionKey) {
            existingKeys.add(item.labelOptionKey);
          }
          existing.labelOptionKeys = Array.from(existingKeys);
          existing.labelOptionKey = existing.labelOptionKeys[0];

          const existingPortionsArray = existing.portionIndices || (existing.portionIndex ? [existing.portionIndex] : []);
          const existingPortions = new Set(existingPortionsArray);
          if (item.portionIndices) {
            item.portionIndices.forEach(p => existingPortions.add(p));
          } else if (item.portionIndex) {
            existingPortions.add(item.portionIndex);
          }
          existing.portionIndices = Array.from(existingPortions);
          existing.portionIndex = existing.portionIndices[0];

          existing.combinedIds = existing.combinedIds || [existing.id];
          existing.combinedIds.push(item.id);
        } else {
          combinedLabelOptionMap.set(key, { ...item });
        }
      } else {
        nonSpecialItems.push(item);
      }
    });
    
    const combinedSpecialItems = Array.from(combinedSpecialMap.values());
    const combinedLabelOptionItems = Array.from(combinedLabelOptionMap.values());
    return [...combinedSpecialItems, ...combinedLabelOptionItems, ...nonSpecialItems];
  }, []);

  // Categorize items into sections — respects userSection overrides so a
  // user-added item dragged into e.g. "InFeed Options" is grouped with the
  // infeed section here (and split into the options sub-table by renderSection).
  const categorizeItems = useCallback(() => {
    const filtered = data.filter(item => !item.isDeleted);
    const inInfeed = (item) => {
      const sec = getSectionFor(item);
      return sec === 'infeed-primary' || sec === 'infeed-options';
    };
    const inOutfeed = (item) => {
      const sec = getSectionFor(item);
      return sec === 'outfeed-primary' || sec === 'outfeed-options';
    };
    const inNew = (item) => getSectionFor(item) === 'new';
    const infeedItems = combineStockArmItems(filtered.filter(inInfeed));
    const outfeedItems = combineStockArmItems(filtered.filter(inOutfeed));
    const newItems = combineStockArmItems(filtered.filter(inNew));
    return { infeedItems, outfeedItems, newItems };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, combineStockArmItems]);

  const { infeedItems, outfeedItems, newItems } = categorizeItems();

  // Function to convert quoteDetails into table items
  const getAdditionalDetailsItems = useCallback(() => {
  if (!quoteDetails) return [];
  
  const items = [];
  const { installation } = quoteDetails;
  
  // Basic quote details
  if (quoteDetails.quotePrice) items.push({ id: 'quote-price', key: 'Quote Price', value: `$${formatNumberWithCommas(parseFloat(quoteDetails.quotePrice).toFixed(2))}` });
  if (quoteDetails.customerRFQ) items.push({ id: 'customer-rfq', key: 'Customer RFQ', value: quoteDetails.customerRFQ });
  if (discount) items.push({ id: 'standard-discount', key: 'Standard Discount', value: `${discount}%` });
  
  // Installation details
  if (installation) {
    if (installation.deliveryMethod) items.push({ id: 'delivery-method', key: 'Delivery Method', value: installation.deliveryMethod });
    if (installation.installationOption) items.push({ id: 'installation-option', key: 'Installation Option', value: installation.installationOption });
    if (installation.installQuoted) items.push({ id: 'install-quoted', key: 'Install Quoted', value: installation.installQuoted });
    if (installation.techs && installation.techs !== '0') items.push({ id: 'techs', key: '# Service Techs', value: installation.techs });
    if (installation.days && installation.days !== '0') items.push({ id: 'days', key: 'Number of Days', value: installation.days });
    if (installation.dayRate) items.push({ id: 'day-rate', key: 'Service Rate $/Hour (applies on selection of techs and days)', value: `$${formatNumberWithCommas(parseFloat(installation.dayRate).toFixed(2))}` });
    if (installation.laborTotal) items.push({ id: 'labor-total', key: 'Labor Total (Techs × Service Rate × Days × 8 hrs)', value: `$${formatNumberWithCommas(parseFloat(installation.laborTotal).toFixed(2))}` });
    if (installation.airfare) items.push({ id: 'airfare', key: 'Airfare', value: `$${formatNumberWithCommas(parseFloat(installation.airfare).toFixed(2))}` });
    if (installation.carRental) items.push({ id: 'car-rental', key: 'Car Rental', value: `$${formatNumberWithCommas(parseFloat(installation.carRental).toFixed(2))}` });
    const lodgingTotal = (parseFloat(installation.lodgingMeals) || 0) * (parseInt(installation.techs) || 0) * (parseInt(installation.days) || 0);
    if (lodgingTotal > 0) items.push({ id: 'lodging-meals', key: 'Lodging & Meals', value: `$${formatNumberWithCommas(lodgingTotal.toFixed(2))}` });
    if (installation.conveyorShippingPrice) items.push({ id: 'conveyor-shipping-price', key: 'Conveyor Shipping Price', value: `$${formatNumberWithCommas(parseFloat(installation.conveyorShippingPrice).toFixed(2))}` });
    if (installation.installationTotal) items.push({ id: 'installation-total', key: 'Installation Total', value: `$${formatNumberWithCommas(parseFloat(installation.installationTotal).toFixed(2))}` });
    if (installation.mileage) items.push({ id: 'mileage', key: 'Mileage', value: `$${formatNumberWithCommas(parseFloat(installation.mileage).toFixed(2))}` });
    if (installation.travelTimeCost) items.push({ id: 'travel-time-cost', key: 'Travel Time Cost', value: `$${formatNumberWithCommas(parseFloat(installation.travelTimeCost).toFixed(2))}` });
    if (installation.toolShipments) items.push({ id: 'tool-shipments', key: 'Tool Shipments', value: `$${formatNumberWithCommas(parseFloat(installation.toolShipments).toFixed(2))}` });
    if (installation.address) items.push({ id: 'address', key: 'Address', value: installation.address });
    if (installation.city) items.push({ id: 'city', key: 'City', value: installation.city });
    if (installation.state) items.push({ id: 'state', key: 'State', value: installation.state });
    if (installation.zipcode) items.push({ id: 'zipcode', key: 'Zipcode', value: installation.zipcode });
  }
  
  // Other quote details
  if (quoteDetails.color) items.push({ id: 'color', key: 'Conveyor Color', value: quoteDetails.color });
  if (quoteDetails.conveyorDelivery) items.push({ id: 'conveyor-delivery', key: 'Conveyor Delivery', value: quoteDetails.conveyorDelivery });
  if (quoteDetails.deliveryTerms) items.push({ id: 'delivery-terms', key: 'Delivery Terms', value: quoteDetails.deliveryTerms });
  if (quoteDetails.sawDelivery) items.push({ id: 'saw-delivery', key: 'Saw Delivery', value: quoteDetails.sawDelivery });
  if (quoteDetails.inspection) items.push({ id: 'inspection', key: 'Inspection & Acceptance', value: quoteDetails.inspection });
  if (quoteDetails.warranty) items.push({ id: 'warranty', key: 'Warranty', value: quoteDetails.warranty });
  if (quoteDetails.tax) {
    items.push({ id: 'tax', key: 'Sales Tax', value: quoteDetails.tax });
    const taxPct = parseTaxPercent(quoteDetails.tax);
    if (taxPct > 0) {
      const lineItemsTotal = data.reduce((sum, item) => {
        if (item.isDeleted) return sum;
        const lp = parseFloat(item.listPrice || 0);
        const dv = parseFloat(item.discount || discount || 0);
        const qty = parseFloat(item.qty || 0);
        return sum + qty * lp * (1 - dv / 100);
      }, 0);
      const instTotal = parseFloat(installation?.installationTotal) || 0;
      const taxAmt = (lineItemsTotal + instTotal) * (taxPct / 100);
      items.push({ id: 'tax-amount', key: 'Tax Amount', value: `$${formatNumberWithCommas(taxAmt.toFixed(2))}`, isCurrency: true });
    }
  }
  if (quoteDetails.payment) items.push({ id: 'payment', key: 'Payment Terms', value: quoteDetails.payment });
  if (quoteDetails.location) items.push({ id: 'location', key: 'Installation Location', value: quoteDetails.location });
  
  return items;
}, [quoteDetails, data, discount]);

  // Get the additional details items
  const additionalDetailsItems = getAdditionalDetailsItems();
  
  // Use the custom hooks
  const getGlobalIndices = useGlobalIndices(infeedItems, outfeedItems, newItems, additionalDetailsItems);
  const getSectionItemsWithIndices = useSectionItemsWithIndices(getGlobalIndices);
  const { infeed, outfeed, new: newSectionItems, additional } = getSectionItemsWithIndices();
  
  // Set initial open state based on items
  useEffect(() => {
    setOpenSections({
      infeed: infeed.length > 0,
      outfeed: outfeed.length > 0,
      new: isAddingNew || newSectionItems.length > 0,
      additional: additional.length > 0
    });
  }, [infeed.length, outfeed.length, newSectionItems.length, isAddingNew, additional.length]);

  // Open new items section when adding new item
  useEffect(() => {
    if (isAddingNew) {
      setOpenSections(prev => ({ ...prev, new: true }));
    }
  }, [isAddingNew]);

  // Handle new items that should be editable
  useEffect(() => {
    const newItem = data.find(item => item.isNew);
    if (newItem && editingId !== newItem.id) {
      setEditingId(newItem.id);
      
      // Calculate initial actual price based on list price and discount
      const listPrice = newItem.listPrice || 0;
      const discountValue = newItem.discount || discount || 0;
      const actualPrice = listPrice * (1 - (discountValue / 100));
      const extPrice = (newItem.qty || 0) * actualPrice;
      
      setEditedData({
        ...newItem,
        qty: newItem.qty ?? '',
        listPrice: newItem.listPrice ?? '',
        discount: newItem.discount ?? discount ?? '',
        actualPrice: actualPrice.toFixed(2),
        extPrice: extPrice.toFixed(2)
      });
    }
  }, [data, editingId, discount]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleEdit = (item) => {
    if (isAddingNew) {
      toast.error('Please finish editing the current item first');
      return;
    }
    
    // Calculate values for editing
    const listPrice = item.listPrice || 0;
    // Use item's discount first, then global discount
    const discountValue = item.discount || discount || 0;
    const actualPrice = listPrice * (1 - (discountValue / 100));
    const extPrice = (item.qty || 0) * actualPrice;
    
    setEditingId(item.id);
    setEditedData({ 
      ...item,
      qty: item.qty ?? '',
      listPrice: item.listPrice ?? '',
      discount: item.discount ? item.discount : discount ?? '',
      actualPrice: actualPrice.toFixed(2),
      extPrice: extPrice.toFixed(2)
    });
  };

  const handleSave = () => {
    // Only require partName for both new and existing items
    if (!editedData.partName?.trim()) {
      toast.error('Part Number is required');
      return;
    }

    if (onSave) {
      // Normalize number fields once
      const baseFinalData = {
        ...editedData,
        qty: editedData.qty === '' ? undefined : parseFloat(editedData.qty),
        listPrice: editedData.listPrice === '' ? undefined : parseFloat(editedData.listPrice),
        discount: editedData.discount === '' ? undefined : parseFloat(editedData.discount),
        actualPrice: editedData.actualPrice === ''
          ? undefined
          : parseFloat(editedData.actualPrice),
        extPrice: editedData.extPrice === ''
          ? undefined
          : parseFloat(editedData.extPrice),
        isNew: false,
        isEditing: undefined
      };

      const isSpecialGrouped =
        (baseFinalData.specialLabel === 'STK' || baseFinalData.specialLabel === 'TA') &&
        Array.isArray(baseFinalData.combinedIds) &&
        baseFinalData.combinedIds.length > 0;

      if (isSpecialGrouped) {
        // STK: distribute the user's qty as [N, 0, 0, ...] across underlying items
        // AND stamp manualQty=N on every member, so the override survives diagram
        // changes (added/removed portions) — combineStockArmItems reads manualQty
        // as the authoritative merged qty.
        // TA: keep original per-portion qtys (combined qty is derived).
        const { combinedIds, ...rest } = baseFinalData;
        const isSTKGrouped = baseFinalData.specialLabel === 'STK';
        const newCombinedQty = parseFloat(baseFinalData.qty) || 0;

        combinedIds.forEach((originalId, idx) => {
          const originalItem = data.find((it) => it.id === originalId);
          if (!originalItem) return;

          const qtyForThis = isSTKGrouped
            ? (idx === 0 ? newCombinedQty : 0)
            : originalItem.qty;

          const resolvedActualPrice = rest.actualPrice ?? originalItem.actualPrice;
          const extPriceForThis = isSTKGrouped
            ? qtyForThis * (parseFloat(resolvedActualPrice) || 0)
            : (rest.extPrice ?? originalItem.extPrice);

          const updatedItem = {
            ...originalItem,
            qty: qtyForThis,
            // sync editable pricing / description fields from the edited row
            partName: rest.partName,
            partNumber: rest.partNumber,
            description: rest.description,
            listPrice: rest.listPrice ?? originalItem.listPrice,
            discount: rest.discount ?? originalItem.discount,
            actualPrice: resolvedActualPrice,
            extPrice: extPriceForThis
          };

          if (isSTKGrouped) {
            updatedItem.manualQty = newCombinedQty;
          }

          onSave(updatedItem);
        });
      } else {
        // Normal (non-grouped) row update
        onSave(baseFinalData);
      }

      toast.success(
        editedData.isNew
          ? 'New quote detail added successfully'
          : 'Quote detail saved successfully'
      );
    }
    setEditingId(null);
    setEditedData({});
  };

  const handleCancel = () => {
    if (editedData.isNew && onCancelAddNew) {
      onCancelAddNew(editedData.id);
    }
    setEditingId(null);
    setEditedData({});
  };

  // Delete function that works for both new and existing items
  const handleDelete = () => {
    if (!editedData) {
      toast.error('No item to delete');
      return;
    }

    if (editedData.isNew && onCancelAddNew) {
      // For new items, use the existing onCancelAddNew function
      onCancelAddNew(editedData.id);
      toast.success('New item deleted');
    } else {
      // For existing items, mark as deleted and use onSave
      if (onSave) {
        onSave({
          ...editedData,
          isDeleted: true, // Mark as deleted
          qty: editedData.qty === '' ? undefined : parseFloat(editedData.qty),
          listPrice: editedData.listPrice === '' ? undefined : parseFloat(editedData.listPrice),
          discount: editedData.discount === '' ? undefined : parseFloat(editedData.discount),
          actualPrice: editedData.actualPrice === '' 
            ? undefined 
            : parseFloat(editedData.actualPrice),
          extPrice: editedData.extPrice === '' 
            ? undefined 
            : parseFloat(editedData.extPrice),
          isNew: false,
          isEditing: undefined
        });
        toast.success('Item deleted successfully');
      } else {
        toast.error('Delete function not available');
      }
    }
    
    setEditingId(null);
    setEditedData({});
    setShowDeleteConfirm(false);
  };

  const handleChange = (field, value) => {
    const newData = {
      ...editedData,
      [field]: value
    };
    
    // Recalculate actual price when listPrice or discount changes
    if (field === 'listPrice' || field === 'discount') {
      const listPrice = field === 'listPrice' ? value : newData.listPrice;
      const discountValue = field === 'discount' ? value : newData.discount;
      
      const actualPrice = parseFloat(listPrice || 0) * (1 - (parseFloat(discountValue || 0) / 100));
      newData.actualPrice = actualPrice.toFixed(2);
      
      // Also recalculate extended price if quantity is available
      if (newData.qty) {
        const extPrice = parseFloat(newData.qty) * actualPrice;
        newData.extPrice = extPrice.toFixed(2);
      }
    }
    
    // Recalculate extended price when quantity changes
    if (field === 'qty') {
      const actualPrice = parseFloat(newData.actualPrice || 0);
      const extPrice = parseFloat(value || 0) * actualPrice;
      newData.extPrice = extPrice.toFixed(2);
    }
    
    setEditedData(newData);
  };

  const handleClear = () => {
    if (isAddingNew || editingId !== null) {
      toast.error('Please finish editing the current item before clearing');
      return;
    }
    setShowClearConfirm(true);
  };

  const confirmClear = () => {
    if (onClear) {
      onClear();
      toast.success('All items cleared successfully');
    }
    setShowClearConfirm(false);
  };

  const handleExit = () => {
    if (isAddingNew || editingId !== null) {
      toast.error('Please finish editing the current item before exiting');
      return;
    }
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    if (onExit) {
      onExit();
    }
    setShowExitConfirm(false);
  };

  // ─── Drag and Drop ──────────────────────────────────────────────────────
  // Only user-added items (from "+ New Item") are draggable. They can be
  // dropped on any of the five drop zones in VALID_DROP_SECTIONS, OR on a
  // specific row to insert just before that row. The new placement is
  // persisted on the item as `userSection` + `sortOrder` so it survives
  // re-render, save and reload.

  const handleDragStart = (e, item) => {
    if (!isUserMovable(item)) { e.preventDefault(); return; }
    // Block dragging while a row is being added or edited — the in-flight
    // changes would be lost when the dragged row's `onSave` overwrites state.
    if (isAddingNew || editingId !== null) {
      e.preventDefault();
      toast.error('Finish editing the current item before moving rows');
      return;
    }
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(item.id ?? '')); } catch (_) {}
  };

  const handleDragOver = (e) => {
    if (e.preventDefault) e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    return false;
  };

  // Compute the next appended sortOrder for a section: max(effective) + 1.
  // `excludeId` lets us ignore the dragged item itself so its old position
  // doesn't influence the new position.
  const computeAppendOrder = (sectionKey, excludeId) => {
    const inSection = data.filter(it => !it.isDeleted && it.id !== excludeId && getSectionFor(it) === sectionKey);
    if (inSection.length === 0) return 0;
    let max = -Infinity;
    inSection.forEach((it, idx) => {
      const eff = it.sortOrder != null ? Number(it.sortOrder) : idx;
      if (eff > max) max = eff;
    });
    return (max === -Infinity ? 0 : max) + 1;
  };

  // Compute a sortOrder that places the dragged item just before `targetItem`.
  const computeInsertBeforeOrder = (sectionKey, targetItem, excludeId) => {
    const inSection = data.filter(it => !it.isDeleted && it.id !== excludeId && getSectionFor(it) === sectionKey);
    const naturalIdx = inSection.findIndex(it => it.id === targetItem.id);
    const targetEff = targetItem.sortOrder != null ? Number(targetItem.sortOrder) : (naturalIdx >= 0 ? naturalIdx : 0);
    return targetEff - 0.5;
  };

  const placeItemInSection = (item, targetSection, sortOrder) => ({
    ...item,
    userSection: targetSection,
    sortOrder,
    // User-managed items don't belong to a conveyor box and have no special label.
    boxName: undefined,
    specialLabel: undefined,
    isUserAdded: true,
    isNew: false,
    isEditing: undefined,
  });

  const handleSectionDrop = (e, targetSection) => {
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    if (!draggedItem) return false;
    if (!isUserMovable(draggedItem)) {
      toast.error('Only user-added items can be moved');
      setDraggedItem(null);
      return false;
    }
    if (!VALID_DROP_SECTIONS.includes(targetSection)) { setDraggedItem(null); return false; }
    const sortOrder = computeAppendOrder(targetSection, draggedItem.id);
    const updated = placeItemInSection(draggedItem, targetSection, sortOrder);
    if (onSave) {
      onSave(updated);
      toast.success(`Moved to ${sectionLabel(targetSection)}`);
    }
    setDraggedItem(null);
    return false;
  };

  const handleRowDrop = (e, targetItem) => {
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    if (!draggedItem) return false;
    if (!isUserMovable(draggedItem)) {
      toast.error('Only user-added items can be moved');
      setDraggedItem(null);
      return false;
    }
    if (draggedItem.id === targetItem.id) { setDraggedItem(null); return false; }
    const targetSection = getSectionFor(targetItem);
    if (!VALID_DROP_SECTIONS.includes(targetSection)) { setDraggedItem(null); return false; }
    const sortOrder = computeInsertBeforeOrder(targetSection, targetItem, draggedItem.id);
    const updated = placeItemInSection(draggedItem, targetSection, sortOrder);
    if (onSave) {
      onSave(updated);
      toast.success(`Reordered in ${sectionLabel(targetSection)}`);
    }
    setDraggedItem(null);
    return false;
  };

  const handleGeneratePdf = async () => {
    if (isAddingNew || editingId !== null) {
      toast.error('Please finish editing the current item before generating PDF');
      return;
    }
    
    if (data.length === 0) {
      toast.error('No data to generate PDF');
      return;
    }

    if (!customerData?.customer) {
      toast.error('Please select a customer before generating PDF');
      return;
    }

    try {
      setIsGeneratingPdf(true);
      
      // Wait for React to re-render with the updated isGeneratingPdf state
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      
    let sawEl1 = null;

    try {
      const sawEl = sawRef.current || document.getElementById('saw-module');

      if (sawEl) {
        sawEl1 = document.createElement('div');
        sawEl1.className = 'saw-image-only';
        const clonedSaw = sawEl.cloneNode(true);

        // Style box divs
        const boxDivs = clonedSaw.querySelectorAll('div');
        boxDivs.forEach(div => {
          div.style.cssText =
            "width:5rem;height:100%;max-height:100%;box-sizing:border-box;background-color:#5FDB5F;border:4px solid #1e3a8a;display:flex;align-items:center;justify-content:center;text-align:center;";
        });

        // Style spans
        const spans = clonedSaw.querySelectorAll('span');
        spans.forEach(span => {
          span.style.cssText = "color:black;font-weight:bold;font-size:0.875rem;";
          span.textContent = 'SAW';
        });

        // Handle image logic
        const images = clonedSaw.querySelectorAll('img');
        images.forEach(img => {
          if (!img.src || img.src.trim() === '' || img.src.endsWith('/null')) {
            // Replace with box if no image
            const replacementDiv = document.createElement('div');
            replacementDiv.style.cssText =
              "width:5rem;height:100%;max-height:100%;box-sizing:border-box;background-color:#5FDB5F;border:4px solid #1e3a8a;display:flex;align-items:center;justify-content:center;text-align:center;";

            const span = document.createElement('span');
            span.style.cssText = "color:black;font-weight:bold;font-size:0.875rem;";
            span.textContent = 'SAW';

            replacementDiv.appendChild(span);
            img.parentNode.replaceChild(replacementDiv, img);
          } else {
            // Keep valid image and style it
            img.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;";
          }
        });

        sawEl1.appendChild(clonedSaw);
      }
    } catch (e) {
      console.error('Failed to clone saw element', e);
    }
      
  let arrowEl1 = null;
  try {
    const arrowEl = arrowRef.current || document.getElementById('arrow-module');

    if (arrowEl) {
      arrowEl1 = document.createElement('div');
      arrowEl1.className = 'arrow-image-only';
      const clonedArrow = arrowEl.cloneNode(true);
      const allDivs = clonedArrow.querySelectorAll('div');
      allDivs.forEach(div => {
        const styleAttr = div.getAttribute('style') || '';
        if (styleAttr.includes('background-color: rgb(255, 222, 33)') && 
            styleAttr.includes('width: 300px')) {
          div.style.height = '22px';
          const updatedStyle = styleAttr.replace(/height:\s*\d+px/g, 'height: 22px');
          if (updatedStyle !== styleAttr) {
            div.setAttribute('style', updatedStyle);
          } else if (!styleAttr.includes('height:')) {
            div.setAttribute('style', styleAttr + '; height: 22px;');
          }
        }
      });

      arrowEl1.appendChild(clonedArrow);
    }
  } catch (e) {
    console.error('Failed to clone arrow element', e);
  }

      // Prepare data for the template
      const filteredItems = data.filter(item => !item.isDeleted);
      const allItemsWithIndices = getGlobalIndices();
      const infeedItemsForPdf = allItemsWithIndices.filter(item => item.section === 'infeed');
      const outfeedItemsForPdf = allItemsWithIndices.filter(item => item.section === 'outfeed');
      const newItemsForPdf = allItemsWithIndices.filter(item => item.section === 'new');
      const additionalItemsForPdf = allItemsWithIndices.filter(item => item.section === 'additional');
      
      // Calculate grand total — dedupe STK groups so a manualQty override
      // contributes once per group instead of summing per-portion items.
      const seenStkGroupKeys = new Set();
      const subtotal = filteredItems.reduce((sum, item) => {
        const discountValue = item.discount > 0 ? parseFloat(item.discount) : parseFloat(discount || 0);
        const listPrice = parseFloat(item.listPrice || 0);
        const actualPrice = listPrice * (1 - discountValue / 100);
        if (item.specialLabel === 'STK' && item.manualQty != null) {
          const groupKey = `${item.boxName}::${item.partName}::${item.description}`;
          if (seenStkGroupKeys.has(groupKey)) return sum;
          seenStkGroupKeys.add(groupKey);
          return sum + (parseFloat(item.manualQty) || 0) * actualPrice;
        }
        const extPrice = (item.qty || 0) * actualPrice;
        return sum + extPrice;
      }, 0);

      const installationTotal = parseFloat(quoteDetails?.installation?.installationTotal) || 0;
      const pdfTaxPercent = parseTaxPercent(quoteDetails?.tax);
      const pdfTaxAmount = (subtotal + installationTotal) * (pdfTaxPercent / 100);
      const pdfGrandTotal = subtotal + installationTotal + pdfTaxAmount;
      // Capture infeed/outfeed modules as images
      let infeedModuleImages = [];
      let outfeedModuleImages = [];
      let infeedModuleData = [];
      let outfeedModuleData = [];
      let imageResultsMap = new Map();
      try {
        if (typeof window !== 'undefined') {
      //    const { default: html2canvas } = await import('html2canvas');
          const infeedEl = infeedRef?.current || document.getElementById('infeed-modules');
          const outfeedEl = outfeedRef?.current || document.getElementById('outfeed-modules');

          // Collect all ISO filenames first
          let allIsoFilenames = [];
          
          if (infeedEl) {
          //  const canvas = await html2canvas(infeedEl, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
            const moduleNodes = infeedEl.querySelectorAll('[id^="left-module-"]');
            
            // Process each module node and collect filenames
            for (const node of moduleNodes) {
          //    const c = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
              
              // Extract portion index from node ID (e.g., "left-module-0" -> "0")
              const portionIndex = node.id.split('-').pop();
              
              // Get the corresponding item data for this module from leftBoxItems
              const item = leftBoxItems ? leftBoxItems[portionIndex] : null;
              
              let filename = 'Unknown';
              if (item && item.alt) {
                if (item.alt.startsWith('SP-')) {
                  filename = 'SPACER.jpg';
                } else {
                  // Get selected labels for this portion
                  const selectedLabels = item.labels ? item.labels.filter(label => {
                    const key = `${portionIndex}-${label}`;
                    return checkedItems.left.get(key) === true;
                  }) : [];
                  
                  // Generate the actual filename using the same logic as the component
                  filename = generateConveyorFilename(
                    selectedLabels,
                    item.alt,
                    leftBoxConveyorStyle,
                    'left'
                  );
                }
              }
              
              // Create ISO filename by adding "ISO" before the extension
              const isoFilename = filename.replace(/(\.[\w\d]+)$/, 'ISO$1');
              allIsoFilenames.push(isoFilename);
              
              // Check for special selections in this module
              const hasMotor = Array.from(checkedItems.left.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-MT') && value === true
              );
              const hasVerticalRoller = Array.from(checkedItems.left.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-VR') && value === true
              );
              const hasCoolantPan = Array.from(checkedItems.left.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-CP') && value === true
              );
              
              // Get motor HP value if motor is selected
              let motorHpValue = '';
              if (hasMotor && motorSelections.left === portionIndex) {
                const motorVariant = motorVariantByBox.left;
                if (motorVariant === 'motor') motorHpValue = '5HP';
                else if (motorVariant === 'motor1') motorHpValue = '3HP';
              }

              infeedModuleData.push({ 
                id: node.id, 
                filename: filename,
                isoFilename: isoFilename,
                portionIndex: portionIndex,
                motor_yn: hasMotor ? 'YES' : 'NO',
                motor_hp_value: motorHpValue,
                vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO',
                coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO'
              });
            }
          }

          if (outfeedEl) {
        //    const canvas = await html2canvas(outfeedEl, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
            const moduleNodes = outfeedEl.querySelectorAll('[id^="right-module-"]');
            
            for (const node of moduleNodes) {
         //     const c = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
              
              // Extract portion index from node ID
              const portionIndex = node.id.split('-').pop();
              
              // Get the corresponding item data for this module from rightBoxItems
              const item = rightBoxItems ? rightBoxItems[portionIndex] : null;
              
              let filename = 'Unknown';
              if (item && item.alt) {
                if (item.alt.startsWith('SP-')) {
                  filename = 'SPACER.jpg';
                } else {
                  // Get selected labels for this portion
                  const selectedLabels = item.labels ? item.labels.filter(label => {
                    const key = `${portionIndex}-${label}`;
                    return checkedItems.right.get(key) === true;
                  }) : [];
                  
                  // Generate the actual filename
                  filename = generateConveyorFilename(
                    selectedLabels,
                    item.alt,
                    rightBoxConveyorStyle,
                    'right'
                  );
                }
              }
              
              // Create ISO filename by adding "ISO" before the extension
              const isoFilename = filename.replace(/(\.[\w\d]+)$/, 'ISO$1');
              allIsoFilenames.push(isoFilename);
              
              // Check for special selections in this module
              const hasMotor = Array.from(checkedItems.right.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-MT') && value === true
              );
              const hasVerticalRoller = Array.from(checkedItems.right.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-VR') && value === true
              );
              const hasCoolantPan = Array.from(checkedItems.right.entries()).some(([key, value]) => 
                key.startsWith(`${portionIndex}-`) && key.endsWith('-CP') && value === true
              );
              
              // Get motor HP value if motor is selected
              let motorHpValue = '';
              if (hasMotor && motorSelections.right === portionIndex) {
                const motorVariant = motorVariantByBox.right;
                if (motorVariant === 'motor') motorHpValue = '5HP';
                else if (motorVariant === 'motor1') motorHpValue = '3HP';
              }

              outfeedModuleData.push({ 
                id: node.id, 
                filename: filename,
                isoFilename: isoFilename,
                portionIndex: portionIndex,
                motor_yn: hasMotor ? 'YES' : 'NO',
                motor_hp_value: motorHpValue,
                vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO',
                coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO'
              });
            }
          }
          
          // Collect non-ISO filenames so we can refresh presigned URLs for layout images
          const allLayoutFilenames = [
            ...infeedModuleData.map(m => m.filename),
            ...outfeedModuleData.map(m => m.filename)
          ].filter(f => f && f !== 'Unknown');

          // Batch-fetch both ISO images (for module detail pages) and layout images (for fresh presigned URLs)
          const allFilesToFetch = [...new Set([...allIsoFilenames, ...allLayoutFilenames])];
          let imageResults = [];
          if (allFilesToFetch.length > 0) {
            try {
              const layoutImagesResponse = await getLayoutImages(allFilesToFetch);
              if (layoutImagesResponse.success) {
                imageResults = layoutImagesResponse.results;
              }
            } catch (error) {
              console.error('Error getting layout images in batch:', error);
            }
          }

          // Create a map for quick lookup of image results
          imageResults.forEach(result => {
            imageResultsMap.set(result.imageName, result);
          });
          
          // Build infeed module images with the fetched data
          infeedModuleImages = infeedModuleData.map(moduleData => {
            const imageResult = imageResultsMap.get(moduleData.isoFilename);
            return {
              ...moduleData,
              isoImageUrl: imageResult?.url || '',
              moduleDescription: imageResult?.module_description || '',
              part_number: imageResult?.part_number || ''
            };
          });
          
          // Build outfeed module images with the fetched data
          outfeedModuleImages = outfeedModuleData.map(moduleData => {
            const imageResult = imageResultsMap.get(moduleData.isoFilename);
            return {
              ...moduleData,
              isoImageUrl: imageResult?.url || '',
              moduleDescription: imageResult?.module_description || '',
              part_number: imageResult?.part_number || ''
            };
          });
        }
      } catch (e) {
        console.error('Failed to capture layout images', e);
      }
      
      // Collect only the image containers (without options/checkboxes)
      const collectImageContainers = (boxName, boxItems) => {
        const containers = [];
        if (boxItems && typeof boxItems === 'object') {
          Object.keys(boxItems).forEach(portionIndex => {
            const imageContainer = document.getElementById(`${boxName}-module-${portionIndex}`);
            if (imageContainer) {
              containers.push(imageContainer);
            }
          });
        }
        return containers;
      };

      const infeedImageContainers = collectImageContainers('left', leftBoxItems);
      const outfeedImageContainers = collectImageContainers('right', rightBoxItems);

      // Build a map of module element ID -> fresh presigned URL for layout images
      const freshUrlByModuleId = new Map();
      infeedModuleData.forEach(m => {
        const result = imageResultsMap.get(m.filename);
        if (result?.url) freshUrlByModuleId.set(m.id, result.url);
      });
      outfeedModuleData.forEach(m => {
        const result = imageResultsMap.get(m.filename);
        if (result?.url) freshUrlByModuleId.set(m.id, result.url);
      });

      // Helper to clone a container, strip loading="lazy", and refresh presigned URLs
      const cloneForPdf = (container) => {
        const clone = container.cloneNode(true);
        clone.querySelectorAll('img[loading="lazy"]').forEach(img => img.removeAttribute('loading'));
        // Replace expired presigned URL with fresh one
        const moduleId = clone.id;
        const freshUrl = freshUrlByModuleId.get(moduleId);
        if (freshUrl) {
          const mainImg = clone.querySelector('img');
          if (mainImg) mainImg.src = freshUrl;
        }
        return clone;
      };

      // Create a wrapper div containing only the infeed image containers
      const infeedEl1 = document.createElement('div');
      infeedEl1.className = 'infeed-images-only';
      infeedImageContainers.forEach(container => {
        if (container) {
          infeedEl1.appendChild(cloneForPdf(container));
        }
      });

      // Create a wrapper div containing only the outfeed image containers
      const outfeedEl1 = document.createElement('div');
      outfeedEl1.className = 'outfeed-images-only';
      outfeedImageContainers.forEach(container => {
        if (container) {
          outfeedEl1.appendChild(cloneForPdf(container));
        }
      });
      // Generate HTML using the template
      const effectiveQuoteNumber = quoteNumberOverrideRef.current || quoteNumber;
      const htmlContent = generatePdfTemplate({
        items: filteredItems,
        customerData,
        selectedContact,
        quoteNumber: effectiveQuoteNumber,
        discount,
        infeedItems: infeedItemsForPdf,
        outfeedItems: outfeedItemsForPdf,
        newItems: newItemsForPdf,
        additionalItems: additionalItemsForPdf,
        grandTotal: pdfGrandTotal,
        sawDetails,
        workBedHeight,
        // infeedImageBase64,
        // outfeedImageBase64,
        infeedModuleImages,
        outfeedModuleImages,
        infeedEl1,
        outfeedEl1,
        sawEl1,
        arrowEl1,
        quoteDetails: quoteDetails,
        currentUser: user
      });

      const result = await generateQuotePdf(htmlContent, effectiveQuoteNumber);

      if (result.success) {
        setPdfUrl(result.data.url);
        setPdfQuoteNumber(effectiveQuoteNumber);
        setPdfModalOpen(true);
        toast.success('PDF generated successfully');
        quoteNumberOverrideRef.current = null;
        // pendingSaveRef.current (e.g. navigate('/quotes') for update flow,
        // resetCreateForm for new-quote) fires when the user closes the modal.
        // Firing it here would unmount the page or reset the form before the
        // user can interact with the modal.
        return;
      }

      // Handle specific error cases
      if (result.errorType === 'exists') {
        // Keep pendingSaveRef and override intact so the follow-up fires after the user confirms overwrite.
        setShowOverwriteConfirm(true);
      } else {
        pendingSaveRef.current = null;
        quoteNumberOverrideRef.current = null;
        toast.error(result.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      pendingSaveRef.current = null;
      quoteNumberOverrideRef.current = null;
      toast.error('An unexpected error occurred while generating PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

const handleOverwriteConfirm = async () => {
  setShowOverwriteConfirm(false); // Close modal immediately
  
  try {
    setIsGeneratingPdf(true);

    let sawEl1 = null;

    try {
      const sawEl = sawRef.current || document.getElementById('saw-module');

      if (sawEl) {
        sawEl1 = document.createElement('div');
        sawEl1.className = 'saw-image-only';
        const clonedSaw = sawEl.cloneNode(true);

        // Style box divs
        const boxDivs = clonedSaw.querySelectorAll('div');
        boxDivs.forEach(div => {
          div.style.cssText =
            "width:5rem;height:100%;max-height:100%;box-sizing:border-box;background-color:#5FDB5F;border:4px solid #1e3a8a;display:flex;align-items:center;justify-content:center;text-align:center;";
        });

        // Style spans
        const spans = clonedSaw.querySelectorAll('span');
        spans.forEach(span => {
          span.style.cssText = "color:black;font-weight:bold;font-size:0.875rem;";
          span.textContent = 'SAW';
        });

        // Handle image logic
        const images = clonedSaw.querySelectorAll('img');
        images.forEach(img => {
          if (!img.src || img.src.trim() === '' || img.src.endsWith('/null')) {
            // Replace with box if no image
            const replacementDiv = document.createElement('div');
            replacementDiv.style.cssText =
              "width:5rem;height:100%;max-height:100%;box-sizing:border-box;background-color:#5FDB5F;border:4px solid #1e3a8a;display:flex;align-items:center;justify-content:center;text-align:center;";

            const span = document.createElement('span');
            span.style.cssText = "color:black;font-weight:bold;font-size:0.875rem;";
            span.textContent = 'SAW';

            replacementDiv.appendChild(span);
            img.parentNode.replaceChild(replacementDiv, img);
          } else {
            // Keep valid image and style it
            img.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;";
          }
        });

        sawEl1.appendChild(clonedSaw);
      }
    } catch (e) {
      console.error('Failed to clone saw element', e);
    }

    let arrowEl1 = null;
    try {
      const arrowEl = arrowRef.current || document.getElementById('arrow-module');

      if (arrowEl) {
        arrowEl1 = document.createElement('div');
        arrowEl1.className = 'arrow-image-only';
        const clonedArrow = arrowEl.cloneNode(true);
        const allDivs = clonedArrow.querySelectorAll('div');
        allDivs.forEach(div => {
          const styleAttr = div.getAttribute('style') || '';
          if (styleAttr.includes('background-color: rgb(255, 222, 33)') && 
              styleAttr.includes('width: 300px')) {
            div.style.height = '22px';
          }
        });

        arrowEl1.appendChild(clonedArrow);
      }
    } catch (e) {
      console.error('Failed to clone arrow element', e);
    }
    
    // Prepare data for the template
    const filteredItems = data.filter(item => !item.isDeleted);
    const allItemsWithIndices = getGlobalIndices();
    const infeedItemsForPdf = allItemsWithIndices.filter(item => item.section === 'infeed');
    const outfeedItemsForPdf = allItemsWithIndices.filter(item => item.section === 'outfeed');
    const newItemsForPdf = allItemsWithIndices.filter(item => item.section === 'new');
    const additionalItemsForPdf = allItemsWithIndices.filter(item => item.section === 'additional');
    
    // Calculate grand total — dedupe STK groups so a manualQty override
    // contributes once per group instead of summing per-portion items.
    const seenStkGroupKeys = new Set();
    const subtotal = filteredItems.reduce((sum, item) => {
      const discountValue = item.discount > 0 ? parseFloat(item.discount) : parseFloat(discount || 0);
      const listPrice = parseFloat(item.listPrice || 0);
      const actualPrice = listPrice * (1 - discountValue / 100);
      if (item.specialLabel === 'STK' && item.manualQty != null) {
        const groupKey = `${item.boxName}::${item.partName}::${item.description}`;
        if (seenStkGroupKeys.has(groupKey)) return sum;
        seenStkGroupKeys.add(groupKey);
        return sum + (parseFloat(item.manualQty) || 0) * actualPrice;
      }
      const extPrice = (item.qty || 0) * actualPrice;
      return sum + extPrice;
    }, 0);
    
    const installationTotal = parseFloat(quoteDetails?.installation?.installationTotal) || 0;
    const pdfTaxPercent = parseTaxPercent(quoteDetails?.tax);
    const pdfTaxAmount = (subtotal + installationTotal) * (pdfTaxPercent / 100);
    const pdfGrandTotal = subtotal + installationTotal + pdfTaxAmount;
    
    // Capture infeed/outfeed modules as images
    let infeedModuleImages = [];
    let outfeedModuleImages = [];
    let infeedModuleData = [];
    let outfeedModuleData = [];
    let imageResultsMap = new Map();
    try {
      if (typeof window !== 'undefined') {
  //      const { default: html2canvas } = await import('html2canvas');
        const infeedEl = infeedRef?.current || document.getElementById('infeed-modules');
        const outfeedEl = outfeedRef?.current || document.getElementById('outfeed-modules');

        // Collect all ISO filenames first
        let allIsoFilenames = [];

        if (infeedEl) {
       //   const canvas = await html2canvas(infeedEl, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
          const moduleNodes = infeedEl.querySelectorAll('[id^="left-module-"]');
          
          // Process each module node and collect filenames
          for (const node of moduleNodes) {
        //    const c = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
            
            // Extract portion index from node ID
            const portionIndex = node.id.split('-').pop();
            
            // Get the corresponding item data for this module from leftBoxItems
            const item = leftBoxItems ? leftBoxItems[portionIndex] : null;
            
            let filename = 'Unknown';
            if (item && item.alt) {
              if (item.alt.startsWith('SP-')) {
                filename = 'SPACER.jpg';
              } else {
                // Get selected labels for this portion
                const selectedLabels = item.labels ? item.labels.filter(label => {
                  const key = `${portionIndex}-${label}`;
                  return checkedItems.left.get(key) === true;
                }) : [];
                
                // Generate the actual filename
                filename = generateConveyorFilename(
                  selectedLabels,
                  item.alt,
                  leftBoxConveyorStyle,
                  'left'
                );
              }
            }
            
            // Create ISO filename by adding "ISO" before the extension
            const isoFilename = filename.replace(/(\.[\w\d]+)$/, 'ISO$1');
            allIsoFilenames.push(isoFilename);
            
            // Check for special selections in this module
            const hasMotor = Array.from(checkedItems.left.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-MT') && value === true
            );
            const hasVerticalRoller = Array.from(checkedItems.left.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-VR') && value === true
            );
            const hasCoolantPan = Array.from(checkedItems.left.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-CP') && value === true
            );
            
            // Get motor HP value if motor is selected
            let motorHpValue = '';
            if (hasMotor && motorSelections.left === portionIndex) {
              const motorVariant = motorVariantByBox.left;
              if (motorVariant === 'motor') motorHpValue = '5HP';
              else if (motorVariant === 'motor1') motorHpValue = '3HP';
            }

            infeedModuleData.push({ 
              id: node.id, 
              filename: filename,
              isoFilename: isoFilename,
              portionIndex: portionIndex,
              motor_yn: hasMotor ? 'YES' : 'NO',
              motor_hp_value: motorHpValue,
              vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO',
              coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO'
            });
          }
        }

        if (outfeedEl) {
      //    const canvas = await html2canvas(outfeedEl, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
          const moduleNodes = outfeedEl.querySelectorAll('[id^="right-module-"]');
          
          for (const node of moduleNodes) {
      //      const c = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
            
            // Extract portion index from node ID
            const portionIndex = node.id.split('-').pop();
            
            // Get the corresponding item data for this module from rightBoxItems
            const item = rightBoxItems ? rightBoxItems[portionIndex] : null;
            
            let filename = 'Unknown';
            if (item && item.alt) {
              if (item.alt.startsWith('SP-')) {
                filename = 'SPACER.jpg';
              } else {
                // Get selected labels for this portion
                const selectedLabels = item.labels ? item.labels.filter(label => {
                  const key = `${portionIndex}-${label}`;
                  return checkedItems.right.get(key) === true;
                }) : [];
                
                // Generate the actual filename
                filename = generateConveyorFilename(
                  selectedLabels,
                  item.alt,
                  rightBoxConveyorStyle,
                  'right'
                );
              }
            }
            
            // Create ISO filename by adding "ISO" before the extension
            const isoFilename = filename.replace(/(\.[\w\d]+)$/, 'ISO$1');
            allIsoFilenames.push(isoFilename);
            
            // Check for special selections in this module
            const hasMotor = Array.from(checkedItems.right.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-MT') && value === true
            );
            const hasVerticalRoller = Array.from(checkedItems.right.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-VR') && value === true
            );
            const hasCoolantPan = Array.from(checkedItems.right.entries()).some(([key, value]) => 
              key.startsWith(`${portionIndex}-`) && key.endsWith('-CP') && value === true
            );
            
            // Get motor HP value if motor is selected
            let motorHpValue = '';
            if (hasMotor && motorSelections.right === portionIndex) {
              const motorVariant = motorVariantByBox.right;
              if (motorVariant === 'motor') motorHpValue = '5HP';
              else if (motorVariant === 'motor1') motorHpValue = '3HP';
            }

            outfeedModuleData.push({ 
              id: node.id, 
              filename: filename,
              isoFilename: isoFilename,
              portionIndex: portionIndex,
              motor_yn: hasMotor ? 'YES' : 'NO',
              motor_hp_value: motorHpValue,
              vertical_roller_yn: hasVerticalRoller ? 'YES' : 'NO',
              coolant_pan_yn: hasCoolantPan ? 'YES' : 'NO'
            });
          }
        }
        
        // Collect non-ISO filenames so we can refresh presigned URLs for layout images
        const allLayoutFilenames = [
          ...infeedModuleData.map(m => m.filename),
          ...outfeedModuleData.map(m => m.filename)
        ].filter(f => f && f !== 'Unknown');

        // Batch-fetch both ISO images (for module detail pages) and layout images (for fresh presigned URLs)
        const allFilesToFetch = [...new Set([...allIsoFilenames, ...allLayoutFilenames])];
        let imageResults = [];
        if (allFilesToFetch.length > 0) {
          try {
            const layoutImagesResponse = await getLayoutImages(allFilesToFetch);
            if (layoutImagesResponse.success) {
              imageResults = layoutImagesResponse.results;
            }
          } catch (error) {
            console.error('Error getting layout images in batch:', error);
          }
        }

        // Create a map for quick lookup of image results
        imageResults.forEach(result => {
          imageResultsMap.set(result.imageName, result);
        });
        
        // Build infeed module images with the fetched data
        infeedModuleImages = infeedModuleData.map(moduleData => {
          const imageResult = imageResultsMap.get(moduleData.isoFilename);
          return {
            ...moduleData,
            isoImageUrl: imageResult?.url || '',
            moduleDescription: imageResult?.module_description || '',
            part_number: imageResult?.part_number || ''
          };
        });
        // Build outfeed module images with the fetched data
        outfeedModuleImages = outfeedModuleData.map(moduleData => {
          const imageResult = imageResultsMap.get(moduleData.isoFilename);
          return {
            ...moduleData,
            isoImageUrl: imageResult?.url || '',
            moduleDescription: imageResult?.module_description || '',
            part_number: imageResult?.part_number || ''
          };
        });
      }
    } catch (e) {
      console.error('Failed to capture layout images', e);
    }
    
    // Collect only the image containers (without options/checkboxes)
    const collectImageContainers = (boxName, boxItems) => {
      const containers = [];
      if (boxItems && typeof boxItems === 'object') {
        Object.keys(boxItems).forEach(portionIndex => {
          const imageContainer = document.getElementById(`${boxName}-module-${portionIndex}`);
          if (imageContainer) {
            containers.push(imageContainer);
          }
        });
      }
      return containers;
    };

    const infeedImageContainers = collectImageContainers('left', leftBoxItems);
    const outfeedImageContainers = collectImageContainers('right', rightBoxItems);

    // Build a map of module element ID -> fresh presigned URL for layout images
    const freshUrlByModuleId = new Map();
    infeedModuleData.forEach(m => {
      const result = imageResultsMap.get(m.filename);
      if (result?.url) freshUrlByModuleId.set(m.id, result.url);
    });
    outfeedModuleData.forEach(m => {
      const result = imageResultsMap.get(m.filename);
      if (result?.url) freshUrlByModuleId.set(m.id, result.url);
    });

    // Helper to clone a container, strip loading="lazy", and refresh presigned URLs
    const cloneForPdf = (container) => {
      const clone = container.cloneNode(true);
      clone.querySelectorAll('img[loading="lazy"]').forEach(img => img.removeAttribute('loading'));
      // Replace expired presigned URL with fresh one
      const moduleId = clone.id;
      const freshUrl = freshUrlByModuleId.get(moduleId);
      if (freshUrl) {
        const mainImg = clone.querySelector('img');
        if (mainImg) mainImg.src = freshUrl;
      }
      return clone;
    };

    // Create a wrapper div containing only the infeed image containers
    const infeedEl1 = document.createElement('div');
    infeedEl1.className = 'infeed-images-only';
    infeedImageContainers.forEach(container => {
      if (container) {
        infeedEl1.appendChild(cloneForPdf(container));
      }
    });

    // Create a wrapper div containing only the outfeed image containers
    const outfeedEl1 = document.createElement('div');
    outfeedEl1.className = 'outfeed-images-only';
    outfeedImageContainers.forEach(container => {
      if (container) {
        outfeedEl1.appendChild(cloneForPdf(container));
      }
    });

    // Generate HTML using the template
    const effectiveQuoteNumber = quoteNumberOverrideRef.current || quoteNumber;
    const htmlContent = generatePdfTemplate({
      items: filteredItems,
      customerData,
      selectedContact,
      quoteNumber: effectiveQuoteNumber,
      discount,
      infeedItems: infeedItemsForPdf,
      outfeedItems: outfeedItemsForPdf,
      newItems: newItemsForPdf,
      additionalItems: additionalItemsForPdf,
      grandTotal: pdfGrandTotal,
      sawDetails,
      workBedHeight,
     // infeedImageBase64,
     // outfeedImageBase64,
      infeedModuleImages,
      outfeedModuleImages,
      infeedEl1,
      outfeedEl1,
      sawEl1,
     arrowEl1,
     quoteDetails: quoteDetails,
     currentUser: user
    });

    const overwriteResult = await generateQuotePdf(htmlContent, effectiveQuoteNumber, true);

    if (overwriteResult.success) {
      setPdfUrl(overwriteResult.data.url);
      setPdfQuoteNumber(effectiveQuoteNumber);
      setPdfModalOpen(true);
      toast.success('PDF updated successfully');
      quoteNumberOverrideRef.current = null;
      // pendingSaveRef.current fires on modal close (see handleGeneratePdf
      // for the rationale).
    } else {
      pendingSaveRef.current = null;
      quoteNumberOverrideRef.current = null;
      toast.error(overwriteResult.error || 'Failed to overwrite PDF');
    }
  } catch (error) {
    console.error('PDF overwrite error:', error);
    pendingSaveRef.current = null;
    quoteNumberOverrideRef.current = null;
    toast.error('An unexpected error occurred while overwriting PDF');
  } finally {
    setIsGeneratingPdf(false);
  }
};

  const calculateExtPrice = useCallback(
    (item) => {
      if (item.isDeleted) return 0; // Skip deleted items in calculations
      
      if (editingId === item.id) {
        return parseFloat(editedData.extPrice) || 0;
      }

      const listPrice = parseFloat(item.listPrice || 0);
      const discountValue = parseFloat(item.discount || discount || 0);
      const actualPrice = listPrice * (1 - discountValue / 100);
      const qty = parseFloat(item.qty || 0);

      return qty * actualPrice;
    },
    [editingId, editedData, discount]
  );

  const formatCurrency = (value) => {
    if (isNaN(value) || value === null || value === undefined) return '-';
    const numValue = parseFloat(value || 0);
    const formattedNumber = formatNumberWithCommas(numValue.toFixed(2));
    return `$${formattedNumber}`;
  };

  const formatPercentage = (value) => {
    if (isNaN(value) || value === null || value === undefined) return '-';
    const numValue = parseFloat(value || 0);
    const formattedNumber = formatNumberWithCommas(numValue.toFixed(2));
    return `${formattedNumber}%`;
  };

  const renderCell = (item, field, isNumber = false, isCurrency = false, isPercentage = false) => {
    if (editingId === item.id && field !== 'actualPrice' && field !== 'extPrice') {
      // Special handling for text fields that need proper wrapping
      const isTextField = field === 'partName' || field === 'description';
      
      return (
        <div className="w-full">
          <input
            type={isNumber ? 'number' : 'text'}
            value={editedData[field] ?? ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className={`w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              isTextField 
                ? 'whitespace-normal break-all overflow-hidden' 
                : 'whitespace-normal break-words'
            }`}
            step={isNumber ? '0.01' : undefined}
            min={isNumber ? '0' : undefined}
            placeholder={isNumber ? '0' : ''}
            style={{ maxWidth: '100%' }}
          />
        </div>
      );
    }
    
    // For display mode (or for actualPrice/extPrice fields even in edit mode)
    let displayValue = item[field];
    
    if (field === 'actualPrice') {
      // Calculate actual price from list price and discount
      const listPrice = editingId === item.id 
        ? parseFloat(editedData.listPrice || 0)
        : parseFloat(item.listPrice || 0);
      const discountValue = editingId === item.id 
        ? parseFloat(editedData.discount || discount || 0)
        : parseFloat(item.discount || discount || 0);
      displayValue = listPrice * (1 - (discountValue / 100));
    } else if (field === 'extPrice') {
      // Calculate extended price from quantity and actual price
      const listPrice = editingId === item.id 
        ? parseFloat(editedData.listPrice || 0)
        : parseFloat(item.listPrice || 0);
      const discountValue = editingId === item.id 
        ? parseFloat(editedData.discount || discount || 0)
        : parseFloat(item.discount || discount || 0);
      const actualPrice = listPrice * (1 - (discountValue / 100));
      const qty = editingId === item.id 
        ? parseFloat(editedData.qty || 0)
        : parseFloat(item.qty || 0);
      displayValue = qty * actualPrice;
    }
    
    if (isPercentage) {
      const discountValue = editingId === item.id
        ? parseFloat(editedData[field] || discount || 0)
        : parseFloat(item[field] || discount || 0);
      return formatPercentage(discountValue);
    }
    if (isCurrency) return formatCurrency(displayValue);
    if (isNumber) return formatNumberWithCommas(displayValue) || '-';
    
    // For text fields in display mode, allow word breaks but prevent character breaks
    if (field === 'partName' || field === 'description') {
      return (
        <div className="whitespace-normal break-words overflow-hidden">
          {displayValue || '-'}
        </div>
      );
    }
    
    return displayValue || '-';
  };

  const isEditing = editingId !== null;

  useEffect(() => {
    const subtotal = data.reduce((sum, item) => {
      return sum + (calculateExtPrice(item) || 0);
    }, 0);

    // Add installation total if available
    const installationTotal = quoteDetails?.installation?.installationTotal
      ? parseFloat(quoteDetails.installation.installationTotal)
      : 0;

    // Apply sales tax
    const taxPercent = parseTaxPercent(quoteDetails?.tax);
    const taxAmount = (subtotal + installationTotal) * (taxPercent / 100);

    setGrandTotal(subtotal + installationTotal + taxAmount);
  }, [data, discount, editedData, editingId, calculateExtPrice, quoteDetails]);

  // Helper to render a table for a list of items
  const typeLabelFor = (item) => {
    if (item.boxType) return item.boxType;
    const sec = getSectionFor(item);
    if (sec === 'infeed-primary') return 'Infeed';
    if (sec === 'infeed-options') return 'Infeed Opt';
    if (sec === 'outfeed-primary') return 'Outfeed';
    if (sec === 'outfeed-options') return 'Outfeed Opt';
    return '-';
  };

  const renderItemsTable = (items) => {
    if (!items || items.length === 0) return null;

    return (
      <table className="min-w-full text-sm text-left text-gray-800 table-fixed">
        <thead className="bg-gray-50 text-gray-700 uppercase text-xs tracking-wider">
          <tr>
            {headers.map((header, index) => (
              <th key={header} className={`px-4 py-3 font-semibold ${columnWidths[index]} whitespace-normal break-words`} >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const movable = isUserMovable(item);
            // Rows are themselves drop targets so the user can drop a
            // dragged row directly onto a specific position (insert-before).
            const rowDropHandlers = draggedItem ? {
              onDragOver: handleDragOver,
              onDrop: (e) => handleRowDrop(e, item),
            } : {};
            return (
            <tr
              key={item.id}
              {...rowDropHandlers}
              className={`odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-colors ${draggedItem && draggedItem.id !== item.id ? 'hover:outline hover:outline-1 hover:outline-blue-300' : ''}`}
            >
              <td className={`px-4 py-3 ${columnWidths[0]} align-top whitespace-normal break-words`} >
                {item.globalIndex}
              </td>
              <td className={`px-4 py-3 font-medium ${columnWidths[1]} align-top whitespace-normal break-words`} >
                {typeLabelFor(item)}
              </td>
              <td className={`px-4 py-3 ${columnWidths[2]} align-top whitespace-nowrap overflow-hidden`} >
                {renderCell(item, 'partName')}
              </td>
              <td className={`px-4 py-3 ${columnWidths[3]} align-top whitespace-normal break-words overflow-hidden`} >
                {renderCell(item, 'description')}
              </td>
              <td className={`px-4 py-3 ${columnWidths[4]} align-top whitespace-normal break-words`} >
                {renderCell(item, 'qty', true)}
              </td>
              <td className={`px-4 py-3 ${columnWidths[5]} align-top whitespace-normal break-words`} >
                {renderCell(item, 'listPrice', true, true)}
              </td>
              <td className={`px-4 py-3 ${columnWidths[6]} align-top whitespace-normal break-words`} >
                {renderCell(item, 'discount', true, false, true)}
              </td>
              <td className={`px-4 py-3 ${columnWidths[7]} align-top whitespace-normal break-words`} >
                {renderCell(item, 'actualPrice', true, true)}
              </td>
              <td className={`px-4 py-3 ${columnWidths[8]} align-top whitespace-normal break-words`} >
                {renderCell(item, 'extPrice', true, true)}
              </td>
              <td className={`px-2 py-3 flex items-center gap-1 ${columnWidths[9]} align-top whitespace-nowrap`} >
                {editingId === item.id ? (
                  <>
                    <button onClick={handleSave} className="p-1 text-green-600 hover:text-green-800" title="Save" >
                      <Save className="h-4 w-4" />
                    </button>
                    {/* Delete button */}
                    <button onClick={() => setShowDeleteConfirm(true)} className="p-1 text-red-600 hover:text-red-800" title="Delete" >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button onClick={handleCancel} className="p-1 text-red-600 hover:text-red-800" title="Cancel" >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-1">
                    {/* Drag handle: user-added items can be dragged into any
                        section (Primary InFeed / InFeed Options / Primary
                        OutFeed / OutFeed Options / New Items) or onto a row
                        to insert at that position. */}
                    {movable && (
                      <span
                        draggable={!isAddingNew && editingId === null}
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragEnd={() => setDraggedItem(null)}
                        className={`p-1 inline-flex ${isAddingNew || editingId !== null ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 cursor-move'}`}
                        title={isAddingNew || editingId !== null ? 'Finish editing first' : 'Drag to move between sections'}
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger className="focus:outline-none" disabled={isAddingNew} >
                        <MoreHorizontal className={`h-4 w-4 ${ isAddingNew ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700' }`} />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white shadow-lg rounded-md border border-gray-200 min-w-[120px]">
                        <DropdownMenuLabel className="px-2 py-1 text-xs font-medium text-gray-500"> Actions </DropdownMenuLabel>
                        <DropdownMenuItem className={`px-3 py-3 text-base hover:bg-gray-100 cursor-pointer ${ isAddingNew ? 'text-gray-400' : '' }`} onClick={() => handleEdit(item)} disabled={isAddingNew} > Edit </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    );
  };

  // Render section function. Each of the five logical drop zones (infeed
  // primary/options, outfeed primary/options, new) is its own wrapper with
  // its own onDrop, so dropping on a sub-table targets that specific section.
  const renderSection = (title, items, sectionType) => {
    // For infeed/outfeed, split into primary and options. `userSection`
    // overrides specialLabel-based defaults so a user-added row can live in
    // either sub-table.
    let primaryItems = items;
    let optionsItems = [];

    if (sectionType === 'infeed' || sectionType === 'outfeed') {
      primaryItems = items.filter((item) => {
        const sec = getSectionFor(item);
        if (sec === `${sectionType}-options`) return false;
        if (sec === `${sectionType}-primary`) return true;
        return !optionSpecialLabels.includes(item.specialLabel);
      });
      optionsItems = items.filter((item) => {
        const sec = getSectionFor(item);
        if (sec === `${sectionType}-options`) return true;
        if (sec === `${sectionType}-primary`) return false;
        return optionSpecialLabels.includes(item.specialLabel);
      });
    }

    // Apply sortOrder within each sub-table so user-dragged items slot in.
    primaryItems = applySortOrder(primaryItems);
    optionsItems = applySortOrder(optionsItems);

    // Determine if the whole section is empty (respecting "new" when adding)
    const hasItems =
      (sectionType === 'infeed' || sectionType === 'outfeed')
        ? primaryItems.length > 0 || optionsItems.length > 0
        : items.length > 0;

    if (!hasItems && !(sectionType === 'new' && isAddingNew)) {
      return null;
    }

    const isOpen = openSections[sectionType];

    const primaryHeading =
      sectionType === 'infeed'
        ? 'Primary Quote InFeed'
        : sectionType === 'outfeed'
        ? 'Primary Quote OutFeed'
        : title;

    const optionsHeading =
      sectionType === 'infeed'
        ? 'Primary Quote InFeed Options'
        : sectionType === 'outfeed'
        ? 'Primary Quote OutFeed Options'
        : `${title} Options`;

    const dropZoneClass = (active) =>
      `transition-colors ${active ? 'outline-dashed outline-2 outline-blue-300 bg-blue-50/40' : ''}`;

    const renderSubTable = (subItems, dropKey, heading, emptyLabel) => {
      const dropProps = draggedItem ? {
        onDragOver: handleDragOver,
        onDrop: (e) => handleSectionDrop(e, dropKey),
      } : {};
      return (
        <div {...dropProps} className={dropZoneClass(!!draggedItem)}>
          <div className="px-4 pt-4 pb-2">
            <h4 className="text-md font-semibold text-gray-800"> {heading} </h4>
          </div>
          {subItems.length > 0 ? (
            renderItemsTable(subItems)
          ) : (
            <div className="px-4 py-4 text-center text-gray-500"> {emptyLabel} </div>
          )}
        </div>
      );
    };

    return (
      <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="bg-gray-100 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={() => toggleSection(sectionType)}
        >
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-600" />
          )}
        </div>

        {isOpen && (
          <>
            {sectionType === 'new' ? (
              (() => {
                const dropProps = draggedItem ? {
                  onDragOver: handleDragOver,
                  onDrop: (e) => handleSectionDrop(e, 'new'),
                } : {};
                return (
                  <div {...dropProps} className={`bg-white ${dropZoneClass(!!draggedItem)}`}>
                    {items.length === 0 ? (
                      <div className="px-4 py-5 text-center text-gray-500"> No {title.toLowerCase()} items </div>
                    ) : (
                      renderItemsTable(applySortOrder(items))
                    )}
                  </div>
                );
              })()
            ) : sectionType === 'infeed' || sectionType === 'outfeed' ? (
              <div className="bg-white">
                {renderSubTable(primaryItems, `${sectionType}-primary`, primaryHeading, `No ${primaryHeading.toLowerCase()} items`)}
                {renderSubTable(optionsItems, `${sectionType}-options`, optionsHeading, `No ${optionsHeading.toLowerCase()} items`)}
              </div>
            ) : (
              <div className="bg-white">
                {items.length === 0 ? (
                  <div className="px-4 py-5 text-center text-gray-500"> No {title.toLowerCase()} items </div>
                ) : (
                  renderItemsTable(items)
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  // Render additional details section
  // Dropdown lists for the inline editor. Fetched the first time EDIT is pressed rather
  // than on mount, so quotes that are only ever viewed don't pay for the extra call.
  const loadDetailOptions = useCallback(async () => {
    if (detailOptionsLoaded) return;
    try {
      const res = await getGlobalOptions();
      if (res?.success) {
        setDetailOptions(prev => ({ ...prev, ...buildDropdownOptions(res.data) }));
        if (res.data?.installation?.length > 0) setDetailInstallationData(res.data.installation[0]);
        setDetailOptionsLoaded(true);
      }
    } catch (err) {
      console.error('Error loading quote detail options:', err);
    }
  }, [detailOptionsLoaded, getGlobalOptions]);

  const startEditingDetails = (e) => {
    e.stopPropagation();
    setDetailsSnapshot(JSON.parse(JSON.stringify(quoteDetails || {})));
    setDetailRowsAtEditStart(additional);
    setOpenSections(prev => ({ ...prev, additional: true }));
    setIsEditingDetails(true);
    loadDetailOptions();
  };

  const saveEditedDetails = (e) => {
    e.stopPropagation();
    // Nothing to push: the edits already live in quoteDetails. This just leaves edit mode
    // and gives the user the confirmation they're looking for.
    setIsEditingDetails(false);
    setDetailsSnapshot(null);
    setDetailRowsAtEditStart([]);
    toast.success('Quote details saved');
  };

  const cancelEditedDetails = (e) => {
    e.stopPropagation();
    if (detailsSnapshot && setQuoteDetails) setQuoteDetails(detailsSnapshot);
    setIsEditingDetails(false);
    setDetailsSnapshot(null);
    setDetailRowsAtEditStart([]);
  };

  const getDetailValue = (path) => (
    path.length === 1 ? quoteDetails?.[path[0]] : quoteDetails?.[path[0]]?.[path[1]]
  );

  const updateDetailValue = (path, value) => {
    if (!setQuoteDetails) return;
    setQuoteDetails(prev => {
      if (path.length === 1) return { ...prev, [path[0]]: value };
      const installation = { ...(prev.installation || {}), [path[1]]: value };
      // Keep the derived totals honest while typing — they feed the grand total and the
      // tax line, so a stale Installation Total would misprice the quote.
      if (INSTALLATION_COST_FIELDS.has(path[1])) {
        installation.laborTotal = computeLaborTotal(installation);
        installation.installationTotal = computeInstallationTotal(installation);
      }
      return { ...prev, installation };
    });
  };

  const getDetailDropdownItems = (spec) => {
    if (spec.optionsKey === 'techs') return getTechOptions(detailInstallationData);
    if (spec.optionsKey === 'days') return getDayOptions(detailInstallationData);
    return detailOptions[spec.optionsKey] || [];
  };

  const renderDetailEditor = (item) => {
    const spec = QUOTE_DETAIL_FIELDS[item.id];

    // Unknown row, or one that's calculated from the others — show it, don't let it be
    // typed over.
    if (!spec || spec.kind === 'computed') {
      return (
        <span className="text-gray-600">
          {item.value}
          <span className="ml-2 text-xs italic text-gray-400">(calculated)</span>
        </span>
      );
    }

    if (spec.kind === 'dropdown') {
      return (
        <Dropdown
          items={getDetailDropdownItems(spec)}
          label=""
          width="w-full"
          className="!mb-0"
          placeholder="Select..."
          selected={getDetailValue(spec.path) || ''}
          onSelect={(value) => {
            if (spec.customType && typeof value === 'string' && value.startsWith('Enter Custom')) {
              setDetailCustomPrompt({ customType: spec.customType, path: spec.path });
            } else {
              updateDetailValue(spec.path, value);
            }
          }}
        />
      );
    }

    const isMoney = spec.kind === 'money';
    return (
      <div>
        <input
          type="text"
          value={getDetailValue(spec.path) ?? ''}
          onChange={(e) => updateDetailValue(spec.path, isMoney ? sanitizeMoneyInput(e.target.value) : e.target.value)}
          placeholder={isMoney ? '0.00' : ''}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        {spec.hint === 'lodging' && (
          <span className="block text-xs text-gray-500 mt-1">
            $ per tech, per day — total ${formatNumberWithCommas(computeLodgingTotal(quoteDetails?.installation).toFixed(2))}
            {' '}({quoteDetails?.installation?.techs || 0} techs × {quoteDetails?.installation?.days || 0} days)
          </span>
        )}
      </div>
    );
  };

  const renderAdditionalDetailsSection = (title, liveItems) => {
    // While editing, keep every row that was on screen when EDIT was pressed, even if its
    // value has been blanked out, and append anything newly filled in.
    const items = isEditingDetails
      ? (() => {
          const live = new Map(liveItems.map(i => [i.id, i]));
          const startIds = new Set(detailRowsAtEditStart.map(i => i.id));
          return [
            ...detailRowsAtEditStart.map(row => live.get(row.id) || { ...row, value: '' }),
            ...liveItems.filter(i => !startIds.has(i.id)),
          ];
        })()
      : liveItems;

    if (items.length === 0) {
      return null;
    }

    const isOpen = openSections.additional;

    // Sum up all dollar-value items for the section total
    const additionalTotal = items.reduce((sum, item) => {
      if (typeof item.value === 'string' && item.value.startsWith('$')) {
        const num = parseFloat(item.value.replace(/[$,]/g, ''));
        return sum + (isNaN(num) ? 0 : num);
      }
      return sum;
    }, 0);

    return (
      <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="bg-gray-100 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={() => toggleSection('additional')}
        >
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          {/* EDIT / SAVE sit on the far side of the title, deliberately large and labelled.
              stopPropagation on every one of them — this header row is also the
              collapse/expand toggle. */}
          <div className="flex items-center gap-3">
            {setQuoteDetails && (isEditingDetails ? (
              <>
                <button
                  onClick={cancelEditedDetails}
                  className="px-4 py-2 text-base font-semibold rounded-lg border border-gray-400 bg-white text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditedDetails}
                  className="flex items-center gap-2 px-5 py-2 text-base font-bold rounded-lg bg-green-600 text-white shadow hover:bg-green-700 transition"
                >
                  <Save className="h-5 w-5" />
                  SAVE
                </button>
              </>
            ) : (
              <button
                onClick={startEditingDetails}
                className="flex items-center gap-2 px-5 py-2 text-base font-bold rounded-lg bg-red-600 text-white shadow hover:bg-red-700 transition"
              >
                <Pencil className="h-5 w-5" />
                EDIT
              </button>
            ))}
            {isOpen ? <ChevronDown className="h-5 w-5 text-gray-600" /> : <ChevronRight className="h-5 w-5 text-gray-600" />}
          </div>
        </div>

        {isOpen && (
          <>
          {isEditingDetails && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 text-base text-gray-800">
              Change any value below, then press <span className="font-bold">SAVE</span>.
              Pressing <span className="font-bold">Create Quote</span> or <span className="font-bold">Update Quote</span> saves these changes too.
              Greyed rows are totals the system works out for you.
            </div>
          )}
          <table className="min-w-full text-sm text-left text-gray-800 table-fixed">
            <thead className="bg-gray-50 text-gray-700 uppercase text-xs tracking-wider">
              <tr>
                {/* Add index column header */}
                <th className={`px-4 py-3 font-semibold ${columnWidths[0]} whitespace-normal break-words`}>
                  Index
                </th>
                <th className="px-4 py-3 font-semibold w-1/3 whitespace-normal break-words">Detail</th>
                <th className="px-4 py-3 font-semibold w-2/3 whitespace-normal break-words">Value</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={`${ item.key === 'Installation Total' || item.key === 'Tax Amount' ? 'bg-green-100 hover:bg-green-200' : 'odd:bg-white even:bg-gray-50 hover:bg-gray-100' } transition-colors`} >
                  {/* Add index column */}
                  <td className={`px-4 py-3 ${columnWidths[0]} align-top whitespace-normal break-words`}>
                    {item.globalIndex}
                  </td>
                  <td className="px-4 py-3 align-top whitespace-normal break-words font-medium">{item.key}</td>
                  <td className="px-4 py-3 align-top whitespace-normal break-words">
                    {isEditingDetails ? renderDetailEditor(item) : item.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {additionalTotal > 0 && (
            <div className="bg-gray-800 px-4 py-3 flex justify-end items-center">
              <span className="text-sm font-medium text-gray-300 mr-2">Additional Costs Total:</span>
              <span className="text-sm font-bold text-white">${formatNumberWithCommas(additionalTotal.toFixed(2))}</span>
            </div>
          )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="overflow-x-auto mt-6 border border-gray-200 rounded-xl shadow-lg bg-white">
      {/* Show message when no data at all */}
      {infeed.length === 0 && outfeed.length === 0 && newSectionItems.length === 0 && additional.length === 0 && !isAddingNew && (
        <div className="px-4 py-8 text-center text-gray-500"> Table has no data </div>
      )}

      {/* Render all sections */}
      {renderSection('Infeed', infeed, 'infeed')}
      {renderSection('Outfeed', outfeed, 'outfeed')}
      {renderSection('New Items', newSectionItems, 'new')}
      {renderAdditionalDetailsSection('Additional Quote Details', additional)}
      
      {/* Buttons */}
      <div className="flex justify-between items-center py-4 bg-gray-50 rounded-b-xl px-4">
        <div className="flex-1"></div>
        
        <div className="flex gap-3 justify-center">
          <button onClick={handleClear} className={`px-4 py-2 text-sm rounded-md transition ${ isAddingNew || isEditing || data.length === 0 ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed' : 'text-gray-700 bg-gray-200 border border-gray-300 hover:bg-gray-300' }`} disabled={isAddingNew || isEditing || data.length === 0} > Clear Items </button>

          <button onClick={handleExit} className={`px-4 py-2 text-sm rounded-md transition ${ isAddingNew || isEditing ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed' : 'text-white bg-red-600 border border-red-700 hover:bg-red-700' }`} disabled={isAddingNew || isEditing} > Exit </button>
        </div>
        
        {/* Grand Total Card */}
        <div className="flex-1 flex justify-end">
          {data.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-md shadow-xs px-3 py-2 text-right">
              <div className="flex items-baseline justify-end gap-1">
                <span className="text-md font-medium text-gray-500">Grand Total:</span>
                <span className="text-md font-bold text-gray-800"> {formatCurrency(grandTotal)} </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmModal isOpen={showClearConfirm} onClose={() => setShowClearConfirm(false)} onConfirm={confirmClear} title="Confirm Clear" description="Are you sure you want to clear all items?" confirmBtnText="Cancel" cancelBtnText="Clear" />

      <ConfirmModal isOpen={showExitConfirm} onClose={() => setShowExitConfirm(false)} onConfirm={confirmExit} title="Confirm Exit" description="Are you sure you want to exit? Any unsaved changes will be lost." confirmBtnText="Cancel" cancelBtnText="Exit" />

      <ConfirmModal isOpen={showOverwriteConfirm} onClose={() => { pendingSaveRef.current = null; quoteNumberOverrideRef.current = null; setShowOverwriteConfirm(false); }} onConfirm={handleOverwriteConfirm} title="PDF Already Exists" description={`A PDF for quote ${quoteNumber} already exists. Would you like to overwrite it?`} confirmBtnText="Cancel" cancelBtnText="Overwrite" isLoading={isGeneratingPdf} disableCancel={isGeneratingPdf} />

      <ConfirmModal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} title="Confirm Delete" description="Are you sure you want to delete this item?" confirmBtnText="Cancel" cancelBtnText="Delete" />

      {/* "Enter Custom ..." prompt for the inline Additional Quote Details editor —
          same component and behaviour as the Additional Quote Details popup. */}
      <CustomModal
        isOpen={!!detailCustomPrompt}
        onCancel={() => setDetailCustomPrompt(null)}
        onConfirm={(value) => {
          if (detailCustomPrompt) updateDetailValue(detailCustomPrompt.path, value);
          setDetailCustomPrompt(null);
        }}
        customType={detailCustomPrompt?.customType}
      />

      <LoadingModal loading={isGeneratingPdf} isGeneratingPdf={isGeneratingPdf}/>

      {pdfModalOpen && (
      <PDFModal pdfUrl={pdfUrl} quoteData={data[0]} sawDetails={sawDetails} brochure={quoteBrochure} customerData={customerData} selectedContact={selectedContact} quoteNumber={pdfQuoteNumber || quoteNumber} onClose={() => {
        setPdfModalOpen(false);
        // Run the deferred post-save callback (navigate or form reset) now
        // that the user is done with the PDF modal.
        if (pendingSaveRef.current) {
          const fn = pendingSaveRef.current;
          pendingSaveRef.current = null;
          fn();
        }
      }} selectedAdditionalEmails={selectedAdditionalEmails} />
      )}
    </div>
  );
});

export default TableComponent;
